import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { authAPI } from '../api/client';

function EyeIcon() {
  return (
    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
    </svg>
  );
}

function EyeOffIcon() {
  return (
    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
    </svg>
  );
}

function Spinner() {
  return (
    <svg className="h-5 w-5 animate-spin" fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
    </svg>
  );
}

const authInputClass =
  'w-full rounded-[20px] border border-black/8 bg-white px-4 py-3.5 text-sm text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-emerald-600 focus:ring-4 focus:ring-emerald-600/10 disabled:cursor-not-allowed disabled:bg-slate-50';

const onboardingPoints = [
  'Build invoices, estimates, and expenses from day one',
  'Bring customers, projects, and reporting into one workflow',
  'Invite your team and keep finance operations organized',
];

function AuthShell({ eyebrow, title, description, children }) {
  return (
    <div className="min-h-screen bg-[#f7f4ef]">
      <div className="grid min-h-screen lg:grid-cols-[1.06fr_0.94fr]">
        <section className="relative hidden overflow-hidden bg-[radial-gradient(circle_at_top_right,_rgba(222,127,50,0.18),_transparent_24%),radial-gradient(circle_at_bottom_left,_rgba(21,107,74,0.2),_transparent_30%),linear-gradient(145deg,#161d1b_0%,#183d31_46%,#245844_100%)] lg:flex">
          <div className="absolute inset-0 opacity-[0.08]" style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,0.8) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.8) 1px, transparent 1px)', backgroundSize: '44px 44px' }} />
          <div className="absolute right-0 top-16 h-72 w-72 rounded-full bg-[#de7f32]/20 blur-3xl" />
          <div className="absolute bottom-0 left-0 h-80 w-80 rounded-full bg-emerald-300/10 blur-3xl" />

          <div className="relative z-10 flex w-full flex-col justify-between p-12 xl:p-16">
            <div className="flex items-center gap-3">
              <div className="flex h-14 w-14 items-center justify-center rounded-[22px] border border-white/10 bg-white/10 text-white shadow-lg backdrop-blur">
                <img src="/logo.png" alt="Solaraith" className="h-8 w-auto" onError={(e) => { e.target.style.display = 'none'; }} />
              </div>
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-emerald-100">Solaraith</p>
                <p className="text-lg font-semibold text-white">Accounts Onboarding</p>
              </div>
            </div>

            <div className="max-w-xl">
              <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-[#f4d8bd]">{eyebrow}</p>
              <h1 className="mt-4 text-5xl font-bold leading-[1.02] tracking-[-0.04em] text-white">
                Set up your finance workspace with a calmer first step.
              </h1>
              <p className="mt-5 max-w-lg text-base leading-7 text-white">
                Create your account, verify your email, and start from a workspace designed for billing, operations, and reporting from the beginning.
              </p>

              <div className="mt-10 space-y-4">
                {onboardingPoints.map((point) => (
                  <div key={point} className="flex items-center gap-3 rounded-[22px] border border-white/10 bg-white/8 px-4 py-4 backdrop-blur-sm">
                    <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-white/10 text-[#f6d5b8]">
                      <CheckIcon />
                    </div>
                    <p className="text-sm font-medium text-white">{point}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-[28px] border border-white/10 bg-white/8 p-5 backdrop-blur-sm">
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-white/82">What You Get</p>
              <div className="mt-4 grid gap-4 sm:grid-cols-3">
                <div>
                  <p className="text-2xl font-bold text-white">Invoices</p>
                  <p className="mt-1 text-xs uppercase tracking-[0.16em] text-white/75">Ready fast</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">Reports</p>
                  <p className="mt-1 text-xs uppercase tracking-[0.16em] text-white/75">Live visibility</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">Teams</p>
                  <p className="mt-1 text-xs uppercase tracking-[0.16em] text-white/75">Shared workflow</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="relative flex min-h-screen items-center justify-center px-5 py-8 sm:px-8 lg:px-12 xl:px-16">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(222,127,50,0.14),_transparent_18%),linear-gradient(180deg,#fcfbf8_0%,#f7f4ef_48%,#efe8df_100%)]" />

          <div className="relative z-10 w-full max-w-2xl">
            <div className="mb-6 flex items-center gap-3 lg:hidden">
              <div className="flex h-12 w-12 items-center justify-center rounded-[20px] bg-emerald-700 text-white shadow-lg">
                <img src="/logo.png" alt="Solaraith" className="h-7 w-auto" onError={(e) => { e.target.style.display = 'none'; }} />
              </div>
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-emerald-700">Solaraith</p>
                <p className="text-base font-semibold text-slate-900">Accounts Onboarding</p>
              </div>
            </div>

            <div className="rounded-[32px] border border-black/5 bg-white/86 p-6 shadow-[0_22px_54px_rgba(23,34,28,0.08)] backdrop-blur-xl sm:p-8">
              <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-emerald-700">{eyebrow}</p>
              <h2 className="mt-3 text-[2rem] font-bold tracking-[-0.04em] text-slate-950 sm:text-[2.2rem]">{title}</h2>
              <p className="mt-2 text-sm leading-6 text-slate-700">{description}</p>
              {children}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}

function ErrorBanner({ message }) {
  if (!message) return null;
  return (
    <div className="mt-6 flex items-start gap-3 rounded-[22px] border border-rose-200 bg-rose-50 px-4 py-3.5 text-sm text-rose-700">
      <svg className="mt-0.5 h-4 w-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
      <span>{message}</span>
    </div>
  );
}

function SuccessBanner({ message }) {
  if (!message) return null;
  return (
    <div className="mt-6 flex items-start gap-3 rounded-[22px] border border-emerald-200 bg-emerald-50 px-4 py-3.5 text-sm text-emerald-700">
      <svg className="mt-0.5 h-4 w-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
      <span>{message}</span>
    </div>
  );
}

function PrimaryBtn({ loading, children, disabled, ...rest }) {
  return (
    <button
      disabled={loading || disabled}
      className="flex w-full items-center justify-center gap-2 rounded-[20px] bg-gradient-to-r from-emerald-700 to-emerald-900 px-6 py-3.5 text-sm font-semibold text-white shadow-[0_18px_30px_rgba(21,107,74,0.22)] transition hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-60"
      {...rest}
    >
      {loading ? <><Spinner />{children}</> : children}
    </button>
  );
}

function StepIndicator({ current }) {
  return (
    <div className="mt-7 flex items-center gap-3">
      {[1, 2].map((step) => (
        <React.Fragment key={step}>
          <div className={`flex h-8 w-8 items-center justify-center rounded-full text-xs font-bold transition ${
            step < current ? 'bg-emerald-700 text-white' : step === current ? 'bg-emerald-700 text-white ring-4 ring-emerald-100' : 'bg-slate-100 text-slate-400'
          }`}>
            {step < current ? <CheckIcon /> : step}
          </div>
          {step < 2 && <div className={`h-px flex-1 max-w-[56px] ${step < current ? 'bg-emerald-700' : 'bg-slate-200'}`} />}
        </React.Fragment>
      ))}
      <span className="ml-2 text-xs font-medium uppercase tracking-[0.18em] text-slate-600">
        {current === 1 ? 'Account Setup' : 'Email Verification'}
      </span>
    </div>
  );
}

function Register({ onRegister }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [step, setStep] = useState(1);
  const [resendTimer, setResendTimer] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    let interval;
    if (resendTimer > 0) {
      interval = setInterval(() => setResendTimer((prev) => prev - 1), 1000);
    }
    return () => clearInterval(interval);
  }, [resendTimer]);

  const handleRegisterSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (!name.trim()) return setError('Full name is required');
    if (!email.trim()) return setError('Email address is required');
    if (!username.trim()) return setError('Username is required');
    if (username.length < 3) return setError('Username must be at least 3 characters');
    if (!/^[a-zA-Z0-9_]+$/.test(username)) return setError('Username: letters, numbers and underscores only');
    if (password.length < 6) return setError('Password must be at least 6 characters');
    if (password !== confirmPassword) return setError('Passwords do not match');
    setLoading(true);
    try {
      await authAPI.register(name, email, password, username);
      setSuccess('Account created! Check your email for the verification code.');
      setVerificationCode('');
      setStep(2);
      setResendTimer(60);
    } catch (err) {
      setError(err.response?.data?.error || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifySubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (!verificationCode || verificationCode.length !== 6) return setError('Please enter the 6-digit verification code');
    setLoading(true);
    try {
      const response = await authAPI.verify(email, verificationCode);
      localStorage.setItem('token', response.data.token);
      onRegister(response.data.token);
      setSuccess('Email verified! Redirecting...');
      setTimeout(() => navigate('/'), 1200);
    } catch (err) {
      setError(err.response?.data?.error || 'Verification failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setError('');
    setLoading(true);
    try {
      await authAPI.resendCode(email);
      setSuccess('Code resent. Check your inbox.');
      setResendTimer(60);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to resend code');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      eyebrow={step === 1 ? 'Create Account' : 'Verify Email'}
      title={step === 1 ? 'Start with a split-screen onboarding flow.' : 'Finish setup with one verification step.'}
      description={step === 1 ? 'Create your workspace credentials and start with a cleaner, more focused first-run experience.' : 'Enter the 6-digit code from your inbox to activate your account and enter the app.'}
    >
      <StepIndicator current={step} />
      <ErrorBanner message={error} />
      <SuccessBanner message={success} />

      {step === 1 ? (
        <>
          <form onSubmit={handleRegisterSubmit} className="mt-7 space-y-5">
            <div className="grid gap-5 sm:grid-cols-2">
              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-700">Full Name</label>
                <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Jane Smith" required disabled={loading} className={authInputClass} />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-700">Username</label>
                <input type="text" value={username} onChange={(e) => setUsername(e.target.value)} placeholder="jane_smith" required disabled={loading} className={authInputClass} />
              </div>
            </div>

            <div>
              <label className="mb-1.5 block text-sm font-medium text-slate-700">Email Address</label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" required disabled={loading} className={authInputClass} />
            </div>

            <div className="grid gap-5 sm:grid-cols-2">
              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-700">Password</label>
                <div className="relative">
                  <input type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Minimum 6 characters" required disabled={loading} className={`${authInputClass} pr-12`} />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} disabled={loading} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 transition hover:text-slate-600">
                    {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                  </button>
                </div>
              </div>

              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-700">Confirm Password</label>
                <input type={showPassword ? 'text' : 'password'} value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="Repeat password" required disabled={loading} className={authInputClass} />
              </div>
            </div>

            <div className="grid gap-3 rounded-[24px] border border-black/5 bg-[#f7f4ef]/80 p-4 text-sm text-slate-600 sm:grid-cols-3">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-emerald-700">Billing</p>
                <p className="mt-1 font-medium text-slate-900">Invoices and estimates</p>
              </div>
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-emerald-700">Ops</p>
                <p className="mt-1 font-medium text-slate-900">Projects and expenses</p>
              </div>
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-emerald-700">Insight</p>
                <p className="mt-1 font-medium text-slate-900">Reports and cash flow</p>
              </div>
            </div>

            <PrimaryBtn type="submit" loading={loading}>Create Account &amp; Send Code</PrimaryBtn>
          </form>

          <p className="mt-7 text-center text-sm text-slate-500">
            Already have an account?{' '}
            <Link to="/login" className="font-semibold text-emerald-700 transition hover:text-emerald-800 hover:underline">
              Sign in
            </Link>
          </p>
        </>
      ) : (
        <>
          <div className="mt-7 rounded-[24px] border border-emerald-100 bg-emerald-50/70 p-4 text-sm text-slate-600">
            <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-emerald-700">Verification Email</p>
            <p className="mt-2">
              We sent a 6-digit code to <span className="font-semibold text-slate-900">{email}</span>.
            </p>
          </div>

          <form onSubmit={handleVerifySubmit} className="mt-6 space-y-5">
            <div>
              <label className="mb-1.5 block text-sm font-medium text-slate-700">Verification Code</label>
              <input
                type="text"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                maxLength={6}
                required
                disabled={loading}
                className="w-full rounded-[20px] border border-black/8 bg-white px-4 py-4 text-center font-mono text-2xl tracking-[0.45em] text-slate-900 outline-none transition focus:border-emerald-600 focus:ring-4 focus:ring-emerald-600/10 disabled:cursor-not-allowed disabled:bg-slate-50"
              />
            </div>

            <PrimaryBtn type="submit" loading={loading} disabled={verificationCode.length !== 6}>
              Verify &amp; Complete Setup
            </PrimaryBtn>
          </form>

          <div className="mt-6 flex items-center justify-between gap-4">
            <button
              type="button"
              onClick={() => { setStep(1); setError(''); setSuccess(''); }}
              className="inline-flex items-center gap-1 text-sm font-medium text-slate-500 transition hover:text-slate-700 hover:underline"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back
            </button>
            <button
              type="button"
              onClick={handleResendCode}
              disabled={resendTimer > 0 || loading}
              className="text-sm font-semibold text-emerald-700 transition hover:text-emerald-800 hover:underline disabled:cursor-not-allowed disabled:text-slate-400"
            >
              {resendTimer > 0 ? `Resend in ${resendTimer}s` : 'Resend Code'}
            </button>
          </div>
        </>
      )}
    </AuthShell>
  );
}

export default Register;
